/**
 * @description
 * Main Platform Sync dialog component with multi-step wizard.
 * Handles client selection, sync options, progress tracking, and results display.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 12, 2025
 * @see admin/src/frontend/pages/Settings.tsx
 */

import React, { useState, useEffect } from 'react';
import { X, RefreshCw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { ClientSelectionList } from './ClientSelectionList';
import { SyncOptionsPanel } from './SyncOptionsPanel';
import { SyncProgressIndicator } from './SyncProgressIndicator';
import { SyncResultsSummary } from './SyncResultsSummary';
import type {
  PlatformClient,
  LinkableClient,
  SyncOptions,
  SyncProgress,
  SyncResult,
} from './types';

interface PlatformSyncDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSyncComplete: (result: SyncResult) => void;
}

/**
 * Main dialog component for syncing clients and users from platform
 */
export const PlatformSyncDialog: React.FC<PlatformSyncDialogProps> = ({
  isOpen,
  onClose,
  onSyncComplete,
}) => {
  const [step, setStep] = useState<'select' | 'options' | 'syncing' | 'results'>('select');
  const [platformClients, setPlatformClients] = useState<PlatformClient[]>([]);
  const [selectedClients, setSelectedClients] = useState<Set<string>>(new Set());
  const [linkableClients, setLinkableClients] = useState<LinkableClient[]>([]);
  const [syncOptions, setSyncOptions] = useState<SyncOptions>({
    createNew: true,
    updateExisting: true,
    provisionDatabase: true,
    syncUsers: true,
    addNewUsers: true,
    updateExistingUsers: true,
  });
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch platform clients on mount
  useEffect(() => {
    if (isOpen) {
      fetchPlatformClients();
    }
  }, [isOpen]);

  /**
   * Fetch unsynced and linkable clients from the API
   */
  const fetchPlatformClients = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/platform-sync/clients', {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch platform clients');
      }

      const data = (await response.json()) as {
        unsyncedClients: PlatformClient[];
        linkableClients: LinkableClient[];
      };
      
      setPlatformClients(data.unsyncedClients || []);
      setLinkableClients(data.linkableClients || []);
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to fetch platform clients';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Execute the sync operation
   */
  const handleSync = async () => {
    if (selectedClients.size === 0) {
      toast.error('Please select at least one client to sync');
      return;
    }

    try {
      setStep('syncing');
      setSyncProgress({
        phase: 'fetching',
        current: 0,
        total: selectedClients.size,
        message: 'Preparing to sync clients...',
      });

      // Sync clients
      setSyncProgress({
        phase: 'syncing_clients',
        current: 0,
        total: selectedClients.size,
        message: 'Syncing clients from platform...',
      });

      const clientSyncRes = await fetch('/api/platform-sync/clients/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientIds: Array.from(selectedClients),
          createNew: syncOptions.createNew,
          updateExisting: syncOptions.updateExisting,
          provisionDatabase: syncOptions.provisionDatabase,
        }),
      });

      if (!clientSyncRes.ok) {
        throw new Error('Failed to sync clients');
      }

      const clientSyncData = (await clientSyncRes.json()) as {
        result: SyncResult['clients'];
      };
      const clientResult = clientSyncData.result;

      // Sync users if enabled
      const userResult: SyncResult['users'] = {
        synced: 0,
        created: 0,
        updated: 0,
        skipped: 0,
        errors: [],
      };

      if (syncOptions.syncUsers) {
        setSyncProgress({
          phase: 'syncing_users',
          current: 0,
          total: selectedClients.size,
          message: 'Syncing users for each client...',
        });

        // Sync users for each client
        for (const clientId of Array.from(selectedClients)) {
          try {
            const userSyncRes = await fetch('/api/platform-sync/users/sync', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                clientId,
                addNew: syncOptions.addNewUsers,
                updateExisting: syncOptions.updateExistingUsers,
              }),
            });

            if (!userSyncRes.ok) {
              throw new Error(`Failed to sync users for client ${clientId}`);
            }

            const userSyncData = (await userSyncRes.json()) as {
              result: SyncResult['users'];
            };

            userResult.synced += userSyncData.result.synced;
            userResult.created += userSyncData.result.created;
            userResult.updated += userSyncData.result.updated;
            userResult.skipped += userSyncData.result.skipped;
            userResult.errors.push(...userSyncData.result.errors);
          } catch (err) {
            userResult.errors.push(`Failed to sync users for client ${clientId}`);
          }
        }
      }

      // Complete
      setSyncProgress({
        phase: 'complete',
        current: selectedClients.size,
        total: selectedClients.size,
        message: 'Sync completed successfully!',
      });

      const finalResult: SyncResult = {
        clients: clientResult,
        users: userResult,
      };

      setSyncResult(finalResult);
      setStep('results');
      onSyncComplete(finalResult);

      toast.success(
        `Synced ${clientResult.synced} clients and ${userResult.synced} users`
      );
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Sync failed';
      setError(errorMsg);
      toast.error(errorMsg);
      setStep('select');
    }
  };

  /**
   * Close dialog and reset state
   */
  const handleClose = () => {
    setStep('select');
    setSelectedClients(new Set());
    setSyncProgress(null);
    setSyncResult(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-200 dark:border-gray-800">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <RefreshCw className="h-6 w-6 text-primary" />
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Sync from Platform
              </h2>
              <p className="text-sm text-muted-foreground">
                {step === 'select' && 'Select clients to sync from platform.byteable.ai'}
                {step === 'options' && 'Configure sync options'}
                {step === 'syncing' && 'Syncing data from platform...'}
                {step === 'results' && 'Sync completed'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={step === 'syncing'}
            className="text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-destructive/10 border border-destructive rounded-lg flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-destructive">Error</p>
                <p className="text-sm text-destructive/80 mt-1">{error}</p>
              </div>
            </div>
          )}

          {step === 'select' && (
            <ClientSelectionList
              platformClients={platformClients}
              linkableClients={linkableClients}
              selectedClients={selectedClients}
              onSelectionChange={setSelectedClients}
              loading={loading}
            />
          )}

          {step === 'options' && (
            <SyncOptionsPanel options={syncOptions} onChange={setSyncOptions} />
          )}

          {step === 'syncing' && syncProgress && (
            <SyncProgressIndicator progress={syncProgress} />
          )}

          {step === 'results' && syncResult && (
            <SyncResultsSummary result={syncResult} />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/50">
          <div className="text-sm text-muted-foreground">
            {step === 'select' && `${selectedClients.size} client(s) selected`}
            {step === 'options' && 'Review and confirm sync options'}
            {step === 'syncing' && 'Please wait...'}
            {step === 'results' && 'Sync completed'}
          </div>
          <div className="flex gap-3">
            {step === 'select' && (
              <>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-foreground bg-background border border-input rounded-md hover:bg-accent"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setStep('options')}
                  disabled={selectedClients.size === 0}
                  className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50"
                >
                  Next
                </button>
              </>
            )}
            {step === 'options' && (
              <>
                <button
                  onClick={() => setStep('select')}
                  className="px-4 py-2 text-sm font-medium text-foreground bg-background border border-input rounded-md hover:bg-accent"
                >
                  Back
                </button>
                <button
                  onClick={handleSync}
                  className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90"
                >
                  Start Sync
                </button>
              </>
            )}
            {step === 'results' && (
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90"
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */